import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Share2 } from 'lucide-react'
import { toast } from 'sonner'
import { Switch } from '@/components/v2/ui/Switch'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/v2/ui/Popover'
import { useAuditShareStatus, useAuditShareActions } from '@/hooks/useAuditShare'
import { SharePopover } from './SharePopover'

interface ShareToggleProps {
  jobId: string
  hasRemake?: boolean
}

export function ShareToggle({ jobId, hasRemake }: ShareToggleProps) {
  const { t } = useTranslation()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const { data: status, isLoading, isError } = useAuditShareStatus(jobId)
  const { create, deactivate } = useAuditShareActions(jobId)

  const isActive = !!(status?.exists && status?.is_active)
  const isPending = create.isPending || deactivate.isPending

  const handleIconClick = async () => {
    if (isActive) {
      setPopoverOpen(prev => !prev)
      return
    }
    try {
      await create.mutateAsync(undefined)
      setPopoverOpen(true)
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : t('amazonListing.share.error')
      toast.error(msg)
    }
  }

  const handleDeactivate = async () => {
    try {
      await deactivate.mutateAsync()
      setPopoverOpen(false)
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : t('amazonListing.share.error')
      toast.error(msg)
    }
  }

  if (isLoading) {
    return <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />
  }

  if (isError) {
    return <span className="text-xs text-red-500">{t('amazonListing.share.loadError')}</span>
  }

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={handleIconClick}
          disabled={isPending}
          className={`flex items-center gap-1.5 text-sm transition-colors ${
            isActive
              ? 'text-green-400 hover:text-green-300'
              : 'text-neutral-400 hover:text-white'
          } disabled:opacity-50`}
          title={t('amazonListing.share.toggleLabel')}
          data-testid="share-toggle"
        >
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Share2 className="h-3.5 w-3.5" />
          )}
          <span className="hidden sm:inline">{t('amazonListing.share.toggleLabel')}</span>
        </button>
      </PopoverTrigger>
      {isActive && status && (
        <PopoverContent align="end" className="w-80">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                {t('amazonListing.share.toggleLabel')}
              </span>
              <Switch
                checked={isActive}
                onCheckedChange={(checked) => { if (!checked) handleDeactivate() }}
                disabled={isPending}
                data-testid="share-switch"
              />
            </div>
            <SharePopover
              shareUrl={status.share_url ?? ''}
              expiresAt={status.expires_at ?? ''}
              hasPassword={status.has_password ?? false}
              includeRemake={status.include_remake ?? false}
              hasRemake={hasRemake ?? false}
              jobId={jobId}
            />
          </div>
        </PopoverContent>
      )}
    </Popover>
  )
}
